// Spotter-grafikkerne som React: samme markup og CSS som public/vmix/*.html,
// men data kommer fra grafik-strømmen og følger den valgte kamp.
// Blå står altid til venstre og rød til højre.

import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FighterStats, GfxFight, ResultMethod } from "@mma/shared";
import type { GfxCtx } from "../ctx.js";
import "./spotter.css";

const COLOR = { blue: "var(--blue)", red: "var(--red)" } as const;

export const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
const fmtPct = (n: number) => `${Math.round(n * 10) / 10}%`;

function Corners() {
  return (
    <div className="corners">
      <div style={{ background: COLOR.blue }} />
      <div style={{ background: COLOR.red }} />
    </div>
  );
}

function Ratio({ a, b }: { a: number; b: number }) {
  return (
    <>
      <span>{a}</span> <span className="slash">/</span> <span>{b}</span>
    </>
  );
}

// De fem rækker recap og final har til fælles.
function StatRows({ blue, red, strikesLabel }: { blue: FighterStats; red: FighterStats; strikesLabel: string }) {
  const rows: [string, ReactNode, ReactNode][] = [
    [
      strikesLabel,
      <Ratio a={blue.total_strikes_landed} b={blue.total_strikes_thrown} />,
      <Ratio a={red.total_strikes_landed} b={red.total_strikes_thrown} />,
    ],
    ["Accuracy", fmtPct(blue.total_strikes_accuracy_pct), fmtPct(red.total_strikes_accuracy_pct)],
    [
      "Takedowns",
      <Ratio a={blue.takedowns_landed} b={blue.takedowns_attempted} />,
      <Ratio a={red.takedowns_landed} b={red.takedowns_attempted} />,
    ],
    ["Knockdowns", blue.knockdowns, red.knockdowns],
    ["Control time", fmtTime(blue.control_time_sec), fmtTime(red.control_time_sec)],
  ];
  return (
    <>
      {rows.map(([label, l, r]) => (
        <div key={label} className="stat-row">
          <div className="v left tabular">{l}</div>
          <div className="l">{label}</div>
          <div className="v right tabular">{r}</div>
        </div>
      ))}
    </>
  );
}

const ZERO: FighterStats = {
  total_strikes_landed: 0,
  total_strikes_thrown: 0,
  total_strikes_accuracy_pct: 0,
  punches_landed: 0,
  punches_thrown: 0,
  punches_accuracy_pct: 0,
  kicks_landed: 0,
  kicks_thrown: 0,
  kicks_accuracy_pct: 0,
  jab_landed: 0,
  jab_thrown: 0,
  cross_landed: 0,
  cross_thrown: 0,
  hook_landed: 0,
  hook_thrown: 0,
  uppercut_landed: 0,
  uppercut_thrown: 0,
  low_kick_landed: 0,
  low_kick_thrown: 0,
  body_kick_landed: 0,
  body_kick_thrown: 0,
  head_kick_landed: 0,
  head_kick_thrown: 0,
  takedowns_landed: 0,
  takedowns_attempted: 0,
  takedown_accuracy_pct: 0,
  submissions_attempted: 0,
  submissions_successful: 0,
  knockdowns: 0,
  control_time_sec: 0,
};

const statsOf = (f: GfxFight | null) => f?.stats ?? { blue: ZERO, red: ZERO };

export function Recap({ ctx }: { ctx: GfxCtx; texts: Record<string, string> }) {
  const f = ctx.data.fight;
  const { blue, red } = statsOf(f);
  return (
    <div className={`stage card${f ? "" : " empty"}`}>
      <div className="recap-header">
        <div className="eyebrow">STATS</div>
        <div className="gold-line" />
      </div>
      <div className="names-row">
        <div className="name left">{f?.blue.name ?? "FIGHTER A"}</div>
        <div className="vs">vs</div>
        <div className="name right">{f?.red.name ?? "FIGHTER B"}</div>
      </div>
      <div className="stats-table">
        <StatRows blue={blue} red={red} strikesLabel="Strikes" />
      </div>
      <Corners />
    </div>
  );
}

const STRIKES = [
  ["jab", "Jab"],
  ["cross", "Cross"],
  ["hook", "Hook"],
  ["uppercut", "Uppercut"],
  ["head_kick", "Head Kick"],
  ["body_kick", "Body Kick"],
  ["low_kick", "Low Kick"],
] as const;

export function Breakdown({ ctx }: { ctx: GfxCtx; texts: Record<string, string> }) {
  const f = ctx.data.fight;
  const { blue, red } = statsOf(f);
  const landed = (s: FighterStats, key: (typeof STRIKES)[number][0]) => s[`${key}_landed`];
  // Samme skala for alle søjler: den højeste værdi på tværs af slagtyper.
  const max = Math.max(1, ...STRIKES.flatMap(([k]) => [landed(blue, k), landed(red, k)]));
  return (
    <div className={`stage card${f ? "" : " empty"}`}>
      <div className="header">
        <div className="eyebrow">Strike Breakdown</div>
        <div className="gold-line" />
      </div>
      <div className="names">
        <div className="name left">{f?.blue.name ?? "FIGHTER A"}</div>
        <div className="center">vs</div>
        <div className="name right">{f?.red.name ?? "FIGHTER B"}</div>
      </div>
      <div className="rows">
        {STRIKES.map(([key, label]) => (
          <div key={key} className="row">
            <div className="num left tabular">{landed(blue, key)}</div>
            <div className="bar-wrap">
              <div
                className="bar left"
                style={{ width: `${(landed(blue, key) / max) * 100}%`, ["--bar-color" as string]: "#1d4ed8" }}
              />
            </div>
            <div className="lbl">{label}</div>
            <div className="bar-wrap">
              <div
                className="bar right"
                style={{ width: `${(landed(red, key) / max) * 100}%`, ["--bar-color" as string]: "#ce2030" }}
              />
            </div>
            <div className="num right tabular">{landed(red, key)}</div>
          </div>
        ))}
      </div>
      <Corners />
    </div>
  );
}

const METHOD_LABELS: Record<ResultMethod, string> = {
  ko: "KNOCKOUT",
  tko: "TKO",
  submission: "SUBMISSION",
  decision_unanimous: "UNANIMOUS DECISION",
  decision_split: "SPLIT DECISION",
  decision_majority: "MAJORITY DECISION",
  draw: "DRAW",
  dq: "DISQUALIFICATION",
  no_contest: "NO CONTEST",
};

// Den valgte kamp hvis den er afsluttet, ellers den senest afsluttede.
export function finalFight(ctx: GfxCtx): GfxFight | null {
  return ctx.data.fight?.status === "finished" ? ctx.data.fight : ctx.data.last;
}

export function Final({ ctx }: { ctx: GfxCtx; texts: Record<string, string> }) {
  const f = finalFight(ctx);
  const { blue, red } = statsOf(f);
  const method = f?.resultMethod;
  const winnerName = f?.winner ? f[f.winner].name : null;
  return (
    <div className={`stage card${f ? "" : " empty"}`}>
      <div className="verdict">
        <div className="winner-tag">
          {method ? (winnerName ? `WINNER · ${winnerName}` : METHOD_LABELS[method]) : "FINAL STATS"}
        </div>
        <div className="method">{method ? METHOD_LABELS[method] : "FIGHT ENDED"}</div>
        <div className="when">{method ? (f?.resultRound ? `Round ${f.resultRound}` : "After all rounds") : ""}</div>
        <div className="gold-line" />
      </div>
      <div className="winner-row">
        {(["blue", "red"] as const).map((c) => (
          <div
            key={c}
            className={`fighter${f?.winner === c ? " is-winner" : ""}${f?.winner && f.winner !== c ? " loss" : ""}`}
          >
            <div className="label-tag">{c === "blue" ? "Blue corner" : "Red corner"}</div>
            <div className="nm">{f?.[c].name ?? (c === "blue" ? "FIGHTER A" : "FIGHTER B")}</div>
          </div>
        ))}
      </div>
      <div className="stats">
        <StatRows blue={blue} red={red} strikesLabel="Total strikes" />
      </div>
      <Corners />
    </div>
  );
}

export const EVENT_LABELS = { knockdown: "Knockdown", submission: "Submission", sub_attempt: "Sub attempt" } as const;

export function Flash({ ctx, texts }: { ctx: GfxCtx; texts: Record<string, string> }) {
  const e = ctx.data.fight?.lastEvent;
  return (
    <div className="flash card">
      <div className="kicker">⚡ LIVE EVENT ⚡</div>
      <div className="event">{(texts.event ?? "").toUpperCase()}</div>
      <div className="who">{(texts.who ?? "").toUpperCase()}</div>
      <div className="when">{texts.when}</div>
      <div className="corner-bar" style={e ? { background: COLOR[e.corner] } : undefined} />
    </div>
  );
}

// Strike-tælleren over uret i UR. Wipes ud fra midten når den slås til.
export function StrikesOverlay({ ctx }: { ctx: GfxCtx }) {
  const on = ctx.state.strikes;
  const first = useRef(true);
  const [instant, setInstant] = useState(true);
  useEffect(() => {
    if (first.current) first.current = false;
    else setInstant(false);
  }, [on]);
  const { blue, red } = statsOf(ctx.data.fight);
  return (
    <div className={`spotter-strikes${on ? " on" : ""}${instant ? " instant" : ""}`}>
      <div className="side">
        <div className="num tabular">{blue.total_strikes_landed}</div>
        <div className="pct tabular">{fmtPct(blue.total_strikes_accuracy_pct)}</div>
      </div>
      <div className="middle">
        <div className="lbl">Strikes</div>
        <div className="vs">VS</div>
      </div>
      <div className="side">
        <div className="num tabular">{red.total_strikes_landed}</div>
        <div className="pct tabular">{fmtPct(red.total_strikes_accuracy_pct)}</div>
      </div>
    </div>
  );
}

// Ind: grafikken monteres (CSS-intro spiller). Ud: fade 0,5 s, så fjernes den.
export function SpotterPresence({
  id,
  onAir,
  cue,
  instant,
  children,
}: {
  id: string;
  onAir: boolean;
  cue: number;
  instant: boolean;
  children: ReactNode;
}) {
  const [shown, setShown] = useState(onAir);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (onAir) {
      setShown(true);
      setLeaving(false);
      return;
    }
    setLeaving(true);
    const t = setTimeout(() => {
      setShown(false);
      setLeaving(false);
    }, 500);
    return () => clearTimeout(t);
  }, [onAir, cue]);
  if (!shown) return null;
  return (
    <div key={cue} className={`spotter spotter-${id}${instant ? " no-intro" : ""}${leaving ? " leaving" : ""}`}>
      {children}
    </div>
  );
}
